/**
 * Enhancement Cache System
 *
 * Caches AI-generated enhancements in LocalStorage with TTL (1 hour)
 * Implements LRU eviction when storage limit is reached
 */

const CACHE_KEY = 'enhancement_cache_v1'
const CACHE_TTL = 60 * 60 * 1000 // 1 hour in milliseconds
const MAX_CACHE_SIZE = 50 // Maximum number of cached enhancements

export interface EnhancementCacheEntry {
  faqId: string
  timestamp: number
  expiresAt: number
  enhancement: any // Full enhancement data from API
}

interface CacheStorage {
  [faqId: string]: EnhancementCacheEntry
}

/**
 * Get enhancement from cache if available and not expired
 */
export function getEnhancementFromCache(faqId: string): any | null {
  try {
    const cacheStr = localStorage.getItem(CACHE_KEY)
    if (!cacheStr) return null

    const cache: CacheStorage = JSON.parse(cacheStr)
    const entry = cache[faqId]

    if (!entry) return null

    // Check if expired
    if (Date.now() > entry.expiresAt) {
      console.log(`[Enhancement Cache] Expired: ${faqId}`)
      deleteEnhancementFromCache(faqId)
      return null
    }

    console.log(`[Enhancement Cache] HIT: ${faqId}`)
    return entry.enhancement
  } catch (error) {
    console.error('[Enhancement Cache] Error reading cache:', error)
    return null
  }
}

/**
 * Save enhancement to cache
 */
export function saveEnhancementToCache(faqId: string, enhancement: any): void {
  try {
    const cacheStr = localStorage.getItem(CACHE_KEY)
    const cache: CacheStorage = cacheStr ? JSON.parse(cacheStr) : {}

    // Check cache size and evict oldest if needed
    const cacheEntries = Object.values(cache)
    if (cacheEntries.length >= MAX_CACHE_SIZE) {
      // LRU eviction - remove oldest entry
      const oldest = cacheEntries.reduce((prev, current) =>
        prev.timestamp < current.timestamp ? prev : current
      )
      console.log(`[Enhancement Cache] LRU Eviction: ${oldest.faqId}`)
      delete cache[oldest.faqId]
    }

    // Add new entry
    cache[faqId] = {
      faqId,
      timestamp: Date.now(),
      expiresAt: Date.now() + CACHE_TTL,
      enhancement
    }

    localStorage.setItem(CACHE_KEY, JSON.stringify(cache))
    console.log(`[Enhancement Cache] SAVED: ${faqId} (${Object.keys(cache).length}/${MAX_CACHE_SIZE})`)
  } catch (error) {
    console.error('[Enhancement Cache] Error saving cache:', error)

    // If quota exceeded, clear cache and try again
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.warn('[Enhancement Cache] Quota exceeded, clearing cache')
      clearEnhancementCache()

      // Try saving again with empty cache
      try {
        const freshCache: CacheStorage = {
          [faqId]: {
            faqId,
            timestamp: Date.now(),
            expiresAt: Date.now() + CACHE_TTL,
            enhancement
          }
        }
        localStorage.setItem(CACHE_KEY, JSON.stringify(freshCache))
      } catch (retryError) {
        console.error('[Enhancement Cache] Failed to save after cache clear:', retryError)
      }
    }
  }
}

/**
 * Delete specific enhancement from cache
 */
export function deleteEnhancementFromCache(faqId: string): void {
  try {
    const cacheStr = localStorage.getItem(CACHE_KEY)
    if (!cacheStr) return

    const cache: CacheStorage = JSON.parse(cacheStr)
    delete cache[faqId]

    localStorage.setItem(CACHE_KEY, JSON.stringify(cache))
    console.log(`[Enhancement Cache] DELETED: ${faqId}`)
  } catch (error) {
    console.error('[Enhancement Cache] Error deleting from cache:', error)
  }
}

/**
 * Clear entire enhancement cache
 */
export function clearEnhancementCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY)
    console.log('[Enhancement Cache] CLEARED')
  } catch (error) {
    console.error('[Enhancement Cache] Error clearing cache:', error)
  }
}

/**
 * Check if enhancement is cached (doesn't validate expiry)
 */
export function isEnhancementCached(faqId: string): boolean {
  try {
    const cacheStr = localStorage.getItem(CACHE_KEY)
    if (!cacheStr) return false

    const cache: CacheStorage = JSON.parse(cacheStr)
    return faqId in cache
  } catch (error) {
    return false
  }
}

/**
 * Get cache statistics
 */
export function getEnhancementCacheStats(): { size: number; maxSize: number; entries: string[] } {
  try {
    const cacheStr = localStorage.getItem(CACHE_KEY)
    if (!cacheStr) return { size: 0, maxSize: MAX_CACHE_SIZE, entries: [] }

    const cache: CacheStorage = JSON.parse(cacheStr)
    const entries = Object.keys(cache)

    return {
      size: entries.length,
      maxSize: MAX_CACHE_SIZE,
      entries
    }
  } catch (error) {
    return { size: 0, maxSize: MAX_CACHE_SIZE, entries: [] }
  }
}

/**
 * Clean expired entries from cache
 */
export function cleanExpiredEnhancementCache(): number {
  try {
    const cacheStr = localStorage.getItem(CACHE_KEY)
    if (!cacheStr) return 0

    const cache: CacheStorage = JSON.parse(cacheStr)
    const now = Date.now()
    let cleaned = 0

    for (const [faqId, entry] of Object.entries(cache)) {
      if (now > entry.expiresAt) {
        delete cache[faqId]
        cleaned++
      }
    }

    if (cleaned > 0) {
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache))
      console.log(`[Enhancement Cache] Cleaned ${cleaned} expired entries`)
    }

    return cleaned
  } catch (error) {
    console.error('[Enhancement Cache] Error cleaning cache:', error)
    return 0
  }
}
